import axios from '..';
import type ResType from '../type';
import type {
  DeleteReportCardReqType,
  DeleteSubjectReqType,
  GetSubjectReqType,
  GetTestReqType,
  PatchReportCardReqType,
  PatchSubjectReqType,
  PostReportCardDetailReqType,
  PostReportCardReqType,
  PostSubjectReqType,
  PostTestReqType,
  PutReportCardDetailReqType,
  PutReportCardReqType,
} from './req';
import type {
  ActivityAwardTestResType,
  DeleteSubjectResType,
  GetLangPublishingOrganizationType,
  GetReportCardResType,
  GetReportCardSubjectsResType,
  GetSATPublishingOrganizationType,
  GetSubjectResType,
  HighSchoolTestResType,
  LangaugeTestResType,
  PatchSubjectResType,
  PostReportCardResType,
  PostSubjectResType,
} from './res';

export type Tests = {
  'report-cards': HighSchoolTestResType;
  'language-tests': LangaugeTestResType;
  'standarized-tests': StandardizedTestWithExamInfo;
  'activities-awards': ActivityAwardTestResType;
};

export type ExpandedTests = {
  IB: IB;
  'A-LEVEL': ALEVEL;
  AP: AP;
};

export const getTestSubjectByUserId = <
  TtestType extends keyof Tests,
  Ttest extends keyof Tests[TtestType],
>(
  testType: TtestType,
  test: Ttest & string,
  { userId }: GetTestReqType,
) =>
  axios
    .get<ResType<Tests[TtestType][Ttest]>>(`/${testType}/${test}/users/${userId}`)
    .then((res) => res.data.data);

export const postTest = <TtestType extends keyof PostTestReqType>(
  testType: TtestType,
  body: PostTestReqType[TtestType],
) => axios.post<ResType<string>>(`/${testType}`, body).then((res) => res.data);

export const getReportCardSubjects = () =>
  axios.get<GetReportCardSubjectsResType>('/report-card-subjects').then((res) => res.data);

export const getTestByUserId = <TtestType extends keyof Tests>(
  testType: TtestType,
  { userId }: GetTestReqType,
) => axios.get<ResType<Tests[TtestType]>>(`/${testType}/users/${userId}`).then((res) => res.data);

export const deleteTestById = <TtestType extends keyof Tests>(testType: TtestType, id: string) =>
  axios.delete<ResType<null>>(`/${testType}/${id}`).then((res) => res.data);

export const deleteTestByIdAdmin = <TtestType extends keyof Tests>(
  testType: TtestType,
  id: string,
  userId: string,
) => axios.delete<ResType<null>>(`/${testType}/users/${id}/${userId}`).then((res) => res.data);

export const postReportCardSubjects = (name: string) =>
  axios
    .post<
      ResType<{
        reportCardSubjectId: string;
      }>
    >('/report-card-subjects', { name })
    .then((res) => res.data.data);

export const deleteUserStandardizedTestSubject = (test: keyof ExpandedTests, subjectId: string) =>
  axios.delete<ResType<null>>(`/standarized-tests/${test}/${subjectId}`).then((res) => res.data);

export const deleteUserReportCardDetails = (id: string) =>
  axios.delete<ResType<null>>(`/report-cards-details/${id}`).then((res) => res.data);

export const uploadReportCardTranscript = ({
  userId,
  transcript,
}: {
  userId: string;
  transcript: string;
}) => {
  return axios
    .post<ResType<string>>(`/report-cards/transcript`, { userId, transcript })
    .then((res) => res.data);
};

export const reportCards = {
  get: () => axios.get<GetReportCardResType>('/report-cards'),
  post: (body: PostReportCardReqType) =>
    axios.post<ResType<PostReportCardResType>>('/report-cards', body),
  put: ({ reportCardId, ...body }: PutReportCardReqType) =>
    axios.put<ResType<PutReportCardReqType>>(`/report-cards/${reportCardId}`, body),
  delete: ({ reportCardId }: DeleteReportCardReqType) =>
    axios.delete<ResType<DeleteReportCardReqType>>(`/report-cards/${reportCardId}`),
  admin: {
    users: {
      get: (userId: string) => axios.get<GetReportCardResType>(`/report-cards/users/${userId}`),
    },
  },
};

export const reportCardDetail = {
  post: ({ reportCardId, ...body }: PostReportCardDetailReqType) =>
    axios.post<ResType<PostReportCardDetailReqType>>(`/report-cards-details/${reportCardId}`, body),
  put: ({ reportCardId, ...body }: PutReportCardDetailReqType) =>
    axios.put<ResType<string>>(`/report-cards-details/${reportCardId}`, body),
  ADMIN: {
    post: ({ userId, reportCardId, ...body }: PostReportCardDetailReqType) =>
      axios.post<ResType<PostReportCardDetailReqType>>(
        `/report-cards-details/users/${reportCardId}/${userId}`,
        body,
      ),
    put: ({ userId, reportCardId, ...body }: PutReportCardDetailReqType) =>
      axios.put<ResType<string>>(`/report-cards-details/users/${reportCardId}/${userId}`, body),
  },
};

export const subjectAPI = {
  get: ({ curriculum }: GetSubjectReqType) =>
    axios.get<GetSubjectResType>(`/subjects/${curriculum}`).then((res) => res.data),
  post: (body: PostSubjectReqType) =>
    axios.post<PostSubjectResType>(`/subjects`, body).then((res) => res.data),
  patch: (body: PatchSubjectReqType) =>
    axios.patch<PatchSubjectResType>(`/subjects`, body).then((res) => res.data),
  delete: ({ subjectId }: DeleteSubjectReqType) =>
    axios.delete<DeleteSubjectResType>(`/subjects/${subjectId}`).then((res) => res.data),
};

export const getSATPublishingOrganizations = axios
  .get<GetSATPublishingOrganizationType>(`/standarized-tests/selectable-publishing-organizations`)
  .then((res) => res.data);

export const getLangPublishingOrganizations = axios
  .get<GetLangPublishingOrganizationType>(`/language-tests/selectable-publishing-organizations`)
  .then((res) => res.data);

export const reportCardsAdmin = {
  post: ({ userId, ...body }: PostReportCardReqType) =>
    axios.post<ResType<PostReportCardResType>>(`/report-cards/users/${userId}`, body),
  put: ({ userId, reportCardId, ...body }: PatchReportCardReqType) =>
    axios.put<ResType<PatchReportCardReqType>>(
      `/report-cards/users/${reportCardId}/${userId}`,
      body,
    ),
};
